import React, { useState } from 'react';
import { Terminal, Wrench, Download, RotateCcw } from 'lucide-react';
import { TooltipProvider } from '../components/ui/tooltip';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { SwimlaneDiagram } from '../components/SwimlaneDiagram';
import { Legend } from '../components/Legend';
import { YamlModal } from '../components/YamlModal';
import { InterviewMode } from '../components/InterviewMode';

export const DevOpsVisualizer = () => {
  const [activeTab, setActiveTab] = useState('ideal-flow');
  const [yamlModalOpen, setYamlModalOpen] = useState(false);
  const [runtimeMode, setRuntimeMode] = useState('code-change');

  const handleExportPdf = () => {
    window.print();
  };

  const handleSeeAllFixes = () => {
    setActiveTab('how-to-fix');
  };

  const handleResetAll = () => {
    setActiveTab('ideal-flow');
    setRuntimeMode('code-change');
  };

  return (
    <TooltipProvider>
      <div 
        data-testid="devops-visualizer"
        className="min-h-screen bg-[#f4f4f4]"
      >
        {/* Header */}
        <header className="bg-[#1A3A5C] text-white px-6 py-4">
          <div className="max-w-[1600px] mx-auto flex items-center justify-between">
            <div>
              <h1 className="font-sans text-2xl font-bold tracking-tight">
                DevOps Workflow Visualizer
              </h1>
              <p className="text-white/60 text-sm mt-1">
                CI/CD Pipeline Health Dashboard
              </p>
            </div>
            
            <div className="flex items-center gap-3">
              <Button
                data-testid="action-button-reset-all"
                variant="outline"
                onClick={handleResetAll}
                className="bg-transparent border-white/30 text-white hover:bg-white/10 hover:text-white"
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                Reset All
              </Button>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="max-w-[1600px] mx-auto px-6 py-6">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-4">
              <TabsList className="bg-white border border-[#ddd] p-1 rounded-lg flex-wrap">
                <TabsTrigger
                  data-testid="tab-ideal-flow-trigger"
                  value="ideal-flow"
                  className="px-4 py-2 rounded data-[state=active]:bg-[#007bff] data-[state=active]:text-white transition-all text-sm"
                >
                  Ideal flow
                </TabsTrigger>
                <TabsTrigger
                  data-testid="tab-broken-flow-trigger"
                  value="broken-flow"
                  className="px-4 py-2 rounded data-[state=active]:bg-[#007bff] data-[state=active]:text-white transition-all text-sm"
                >
                  Broken flow
                </TabsTrigger>
                <TabsTrigger
                  data-testid="tab-how-to-fix-trigger"
                  value="how-to-fix"
                  className="px-4 py-2 rounded data-[state=active]:bg-[#007bff] data-[state=active]:text-white transition-all text-sm"
                >
                  How to fix it
                </TabsTrigger>
                <TabsTrigger
                  data-testid="tab-initial-launch-trigger"
                  value="initial-launch"
                  className="px-4 py-2 rounded data-[state=active]:bg-[#007bff] data-[state=active]:text-white transition-all text-sm"
                >
                  Initial launch
                </TabsTrigger>
                <TabsTrigger
                  data-testid="tab-runtime-trigger"
                  value="runtime"
                  className="px-4 py-2 rounded data-[state=active]:bg-[#007bff] data-[state=active]:text-white transition-all text-sm"
                >
                  Runtime
                </TabsTrigger>
                <TabsTrigger
                  data-testid="tab-build-your-own-trigger"
                  value="build-your-own"
                  className="px-4 py-2 rounded data-[state=active]:bg-[#007bff] data-[state=active]:text-white transition-all text-sm"
                >
                  Build your own
                </TabsTrigger>
                <TabsTrigger
                  data-testid="tab-interview-trigger"
                  value="interview"
                  className="px-4 py-2 rounded data-[state=active]:bg-[#007bff] data-[state=active]:text-white transition-all text-sm"
                >
                  Interview mode
                </TabsTrigger>
              </TabsList>
              
              <div className="flex items-center gap-4">
                <Legend />
                
                <div className="flex items-center gap-2">
                  <Button
                    data-testid="action-button-generate-yaml"
                    variant="outline"
                    onClick={() => setYamlModalOpen(true)}
                    size="sm"
                  >
                    <Terminal className="w-4 h-4 mr-2" />
                    Generate YAML
                  </Button>
                  
                  {activeTab === 'broken-flow' && (
                    <Button
                      data-testid="action-button-see-fixes"
                      onClick={handleSeeAllFixes}
                      size="sm"
                      className="bg-[#7F77DD] hover:bg-[#6f65cc] text-white"
                    >
                      <Wrench className="w-4 h-4 mr-2" />
                      See all fixes
                    </Button>
                  )}
                  
                  <Button
                    data-testid="action-button-export-pdf"
                    variant="ghost"
                    onClick={handleExportPdf}
                    size="sm"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Export PDF
                  </Button>
                </div>
              </div>
            </div>

            {/* Runtime Toggle */}
            {activeTab === 'runtime' && (
              <div className="flex items-center justify-center mb-4">
                <div className="flex items-center gap-1 bg-[#eee] p-1 rounded-full border border-[#ccc]">
                  <button
                    data-testid="runtime-toggle-code-change"
                    onClick={() => setRuntimeMode('code-change')}
                    className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${
                      runtimeMode === 'code-change'
                        ? 'bg-[#007bff] text-white'
                        : 'text-[#6c757d] hover:text-[#1A3A5C]'
                    }`}
                  >
                    Code change only
                  </button>
                  <button
                    data-testid="runtime-toggle-new-service"
                    onClick={() => setRuntimeMode('new-service')}
                    className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${
                      runtimeMode === 'new-service'
                        ? 'bg-[#007bff] text-white'
                        : 'text-[#6c757d] hover:text-[#1A3A5C]'
                    }`}
                  >
                    New service deployment
                  </button>
                </div>
              </div>
            )}

            {/* Tab Contents */}
            <div className="bg-white rounded-lg border border-[#eee] overflow-hidden shadow-sm">
              <TabsContent value="ideal-flow" className="m-0">
                <SwimlaneDiagram viewMode="ideal" showTooltips={false} />
              </TabsContent>
              
              <TabsContent value="broken-flow" className="m-0">
                <SwimlaneDiagram viewMode="broken" showTooltips={false} showChickenEggBracket={true} />
              </TabsContent>
              
              <TabsContent value="how-to-fix" className="m-0">
                <SwimlaneDiagram viewMode="broken" showTooltips={true} showChickenEggBracket={true} />
              </TabsContent>

              <TabsContent value="initial-launch" className="m-0">
                <SwimlaneDiagram viewMode="initial-launch" showTooltips={false} showChickenEggBracket={true} />
              </TabsContent>

              <TabsContent value="runtime" className="m-0">
                <SwimlaneDiagram 
                  viewMode="runtime" 
                  runtimeMode={runtimeMode}
                  showTooltips={false} 
                />
              </TabsContent>

              <TabsContent value="build-your-own" className="m-0 p-6">
                <div className="text-center mb-4">
                  <h3 className="text-lg font-semibold text-[#1A3A5C] mb-1">
                    Build Your Own Pipeline
                  </h3>
                  <p className="text-sm text-[#6c757d]">
                    Drag and drop tiles to customize your workflow
                  </p>
                </div>
                <SwimlaneDiagram viewMode="ideal" showTooltips={false} draggable={true} />
              </TabsContent>

              <TabsContent value="interview" className="m-0 p-4">
                <InterviewMode />
              </TabsContent>
            </div>
          </Tabs>

          {/* Footer Info */}
          <div className="mt-4 text-center">
            <p className="text-xs text-[#1A3A5C]/50">
              {activeTab === 'how-to-fix' && (
                <span className="text-[#7F77DD] font-semibold">
                  Hover over broken or at-risk nodes to see detailed fixes
                </span>
              )}
              {activeTab === 'ideal-flow' && (
                <span>
                  This tab shows the desired, fully optimized DevOps workflow
                </span>
              )}
              {activeTab === 'broken-flow' && (
                <span>
                  This tab illustrates the current state of the workflow, highlighting common issues
                </span>
              )}
              {activeTab === 'initial-launch' && (
                <span>
                  Focuses on the state of the workflow during the first deployment
                </span>
              )}
              {activeTab === 'runtime' && (
                <span>
                  {runtimeMode === 'code-change' 
                    ? 'Code changes skip infrastructure setup — dimmed tiles are not needed'
                    : 'Full workflow for deploying a new service'
                  }
                </span>
              )}
              {activeTab === 'build-your-own' && (
                <span>
                  Allows for experimentation and custom workflow arrangement
                </span>
              )}
              {activeTab === 'interview' && (
                <span>
                  Assess candidates by having them arrange tiles into the correct workflow
                </span>
              )}
            </p>
          </div>
        </main>

        {/* YAML Modal */}
        <YamlModal open={yamlModalOpen} onOpenChange={setYamlModalOpen} />
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          header, button, [role="tablist"], .interview-ui {
            display: none !important;
          }
          main {
            padding: 0 !important;
            max-width: none !important;
          }
          .bg-white {
            box-shadow: none !important;
            border: none !important;
          }
        }
      `}</style>
    </TooltipProvider>
  );
};

export default DevOpsVisualizer;
